import {
  REPORT_VIEW_TYPES,
  RENDERABLE_CHART_TYPES,
  type ChartType,
  type ReportViewPreferenceV1,
  type ReportViewType,
} from '../models/chart.types'

export interface ResolveReportViewSelectionInput {
  saved?: unknown
  defaultView: ReportViewType
  defaultChartType: ChartType
  allowedViews: readonly ReportViewType[]
  availableChartTypes: readonly ChartType[]
}

function isReportViewType(value: unknown): value is ReportViewType {
  return (REPORT_VIEW_TYPES as readonly unknown[]).includes(value)
}

function isChartType(value: unknown): value is ChartType {
  return (RENDERABLE_CHART_TYPES as readonly unknown[]).includes(value)
}

export function parseReportViewPreference(value: unknown): ReportViewPreferenceV1 | null {
  if (typeof value !== 'object' || value === null) return null
  const candidate = value as Record<string, unknown>
  if (
    candidate.version !== 1 ||
    !isReportViewType(candidate.view) ||
    !isChartType(candidate.chartType)
  ) {
    return null
  }
  return { version: 1, view: candidate.view, chartType: candidate.chartType }
}

/** Stable order: valid persisted value → valid section default → first configured capability. */
export function resolveReportViewSelection(
  input: ResolveReportViewSelectionInput,
): ReportViewPreferenceV1 {
  const saved = parseReportViewPreference(input.saved)
  const allowedViews = [...new Set(input.allowedViews)]
  const chartTypes = [...new Set(input.availableChartTypes)]

  const chartType =
    (saved && chartTypes.includes(saved.chartType) ? saved.chartType : null) ??
    (chartTypes.includes(input.defaultChartType) ? input.defaultChartType : null) ??
    chartTypes[0] ??
    input.defaultChartType

  const requestedView =
    (saved && allowedViews.includes(saved.view) ? saved.view : null) ??
    (allowedViews.includes(input.defaultView) ? input.defaultView : null) ??
    allowedViews[0] ??
    input.defaultView
  const view =
    requestedView === 'chart' && chartTypes.length === 0 && allowedViews.includes('table')
      ? 'table'
      : requestedView

  return { version: 1, view, chartType }
}
